import * as React from 'react';
import { AutomationType } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import { BuildDistributionUpdatable } from '../../actions/updatable';

import { RootState } from '../../reducers/reducers';
import { selectBuildDistributionBucketById } from '../../selectors/selectors';
import { BucketComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { tryUpdateBuildDistributionBucket } from '../../actions/buildDistribution';
import { AutomationSettings } from './AutomationSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { AutomationSettingsContext } from '@/helpers/automationHelpers';

interface BucketAutomationSettingsProps extends BucketComponentProps {
    open: boolean;
    automationType: AutomationType;
    onClose: () => void;
    onAfterClose: () => void;
}

export const BucketAutomationSettings: React.FC<BucketAutomationSettingsProps> = (props) => {
    const dispatch = useDispatch();

    const bucket = useSelector((state: RootState) => selectBuildDistributionBucketById(state, props.bucketId));
    const bucketAutomations = bucket?.automations ?? [];
    const automation = bucketAutomations.find((automation) => automation.type === props.automationType);

    const updateBucketAutomationEnabledIsLoading = useIsLoading(
        BuildDistributionUpdatable.UpdateBuildDistroBucketAutomationSettings,
        bucket?.id + automation?.type,
    );

    const updateBucketAutomationEnabledSuccess = useUpdated(
        BuildDistributionUpdatable.UpdateBuildDistroBucketAutomationSettings,
        bucket?.id + automation?.type,
    );
    const updateBucketAutomationEnabledError = useError(
        BuildDistributionUpdatable.UpdateBuildDistroBucketAutomationSettings,
        bucket?.id + automation?.type,
    );

    React.useEffect(() => {
        if (updateBucketAutomationEnabledSuccess) {
            globalMessage.success('Updated bucket automation settings!');

            props.onClose();
        }
    }, [updateBucketAutomationEnabledSuccess]);

    React.useEffect(() => {
        if (updateBucketAutomationEnabledError) {
            globalMessage.error(updateBucketAutomationEnabledError);
        }
    }, [updateBucketAutomationEnabledError]);

    return (
        automation && (
            <AutomationSettings
                {...props}
                onAutomationSelected={null}
                sameTypeAutomations={[]}
                automation={automation}
                isLoading={updateBucketAutomationEnabledIsLoading}
                context={AutomationSettingsContext.App}
                onSubmit={(request) => {
                    dispatch(
                        tryUpdateBuildDistributionBucket(
                            BuildDistributionUpdatable.UpdateBuildDistroBucketAutomationSettings,
                            {
                                ...request,
                                bucketId: bucket.id,
                            },
                        ),
                    );
                }}
            />
        )
    );
};
